import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDistributionLists } from "features/Settings/hooks";
import { formatDistributionListHandle } from "utils/distribution-list.util";

const FOLDER_NAME_PATTERN = /^[A-Za-z0-9()\-_&#\s]+$/;

export default function SubmissionFolderDialog({
  open,
  onClose,
  onSubmit,
  folder = null,
  saving = false,
}) {
  const { distributionLists, loading: listsLoading } = useDistributionLists();
  const [name, setName] = useState("");
  const [distributionListId, setDistributionListId] = useState("");
  const [criteria, setCriteria] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(folder?.folderName_chr || "");
    setDistributionListId(folder?.distributionListID ?? "");
    setCriteria(folder?.criteria_chr || "");
    setNameTouched(false);
  }, [folder, open]);

  const nameError = name.length > 0 && !FOLDER_NAME_PATTERN.test(name);
  const validName = name.trim().length > 0 && !nameError;
  const canSubmit = validName && Boolean(distributionListId) && !saving;
  const title = folder ? "Edit Submission Folder" : "Create Submission Folder";

  const submit = async () => {
    setNameTouched(true);
    if (!canSubmit) return;
    await onSubmit({
      folderName: name.trim(),
      distributionListID: Number(distributionListId),
      criteria: criteria.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            autoFocus
            label="Folder Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => setNameTouched(true)}
            error={nameTouched && (!validName || nameError)}
            helperText={
              nameTouched && (!validName || nameError)
                ? "Use letters, numbers, spaces, and ( ) - _ & # only."
                : ""
            }
            inputProps={{ maxLength: 150 }}
            required
          />
          <FormControl
            required
            size="small"
            variant="outlined"
            disabled={listsLoading}
          >
            <InputLabel>Distribution List</InputLabel>
            <Select
              label="Distribution List"
              value={distributionListId}
              onChange={(event) => setDistributionListId(event.target.value)}
              renderValue={(value) => {
                const selected = distributionLists.find((list) => list.id === value);
                return selected?.name || formatDistributionListHandle(selected?.slug) || "";
              }}
            >
              {distributionLists.map((list) => (
                <MenuItem key={list.id} value={list.id}>
                  <Stack gap={0}>
                    <Typography variant="body1">{list.name || list.slug}</Typography>
                    {list.slug && (
                      <Typography variant="body2" sx={{ color: "grey.600", fontSize: "1.2rem" }}>
                        {formatDistributionListHandle(list.slug)}
                      </Typography>
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Link href="/profile/preferences?id=distribution-lists" underline="always">
            Create a Distribution List in Preferences
          </Link>
          <TextField
            label="Submission Criteria"
            value={criteria}
            onChange={(event) => setCriteria(event.target.value.slice(0, 300))}
            helperText={`${criteria.length}/300 characters`}
            multiline
            minRows={3}
            inputProps={{ maxLength: 300 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={!canSubmit}>
          {saving ? "Saving…" : folder ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
